<template>
	<form class="main-wrapper mt-4 mb-3" @submit.prevent="handleSubmit">
		<BannerGeneral
			:id="id"
			ref="general"
			:name="name"
			:priority="priority"
			:status="status"
			:widget-type="widgetType"
			name-label="Hero banner name*"
			@update:name="name = $event"
			@update:priority="priority = $event"
			@update:status="status = $event"
		/>

		<BannerDate
			ref="date"
			:status="status"
			:start-date="startDate"
			:start-time="startTime"
			:end-date="endDate"
			:end-time="endTime"
			@update:startDate="startDate = $event"
			@update:startTime="startTime = $event"
			@update:endDate="endDate = $event"
			@update:endTime="endTime = $event"
		/>

		<BannerContentWrapper
			ref="content"
			:required-link="!!desktopBannerTh && !!desktopBannerEn && !!desktopBannerEn && !!desktopBannerTh"
			:banner-link="bannerLink"
			desktop-img="/assets/images/hero-banner-desktop.svg"
			mobile-img="/assets/images/hero-banner-mobile.svg"
			@update:bannerLink="bannerLink = $event"
		>
			<template #title>
				Hero banner
			</template>
			<template #detail>
				Banner will be displayed on the top of homepage. Using 2 sizes
				of banner for desktop and mobile.
			</template>

			<BaseMediaSelectorBox
				ref="desktopBannerTh"
				v-model="desktopBannerTh"
				title="Desktop banner TH"
				no-image-text="No banner"
				show-helper
				width="1400"
				height="700"
				class="mt-4"
				required
			/>

			<BaseMediaSelectorBox
				ref="desktopBannerEn"
				v-model="desktopBannerEn"
				title="Desktop banner EN"
				no-image-text="No banner"
				show-helper
				width="1400"
				height="700"
				class="mt-4"
				required
			/>

			<div>
				<Label>Select desktop background color*</Label>
				<BaseColorPicker v-model="desktopBackgroundColor" />
			</div>

			<BaseMediaSelectorBox
				ref="mobileBannerTh"
				v-model="mobileBannerTh"
				title="Mobile banner TH"
				no-image-text="No banner"
				show-helper
				width="600"
				height="500"
				class="mt-2"
				required
			/>

			<BaseMediaSelectorBox
				ref="mobileBannerEn"
				v-model="mobileBannerEn"
				title="Mobile banner EN"
				no-image-text="No banner"
				show-helper
				width="600"
				height="500"
				class="mt-2"
				required
			/>

			<div>
				<Label>Select mobile background color*</Label>
				<BaseColorPicker v-model="mobileBackgroundColor" />
			</div>
		</BannerContentWrapper>

		<BaseActionPanelStickyFooter
			:disabled-confirm="isLoading || isUpdating"
			:is-edit="isEditMode"
			:remove-text="isEditMode ? 'Remove banner' : null"
			@onConfirm="handleSubmit"
			@onCancel="$router.push({ name: 'BannerHeroList'})"
			@onRemove="isEditMode ? $refs['modal-remove'].open() : null"
		/>

		<BaseModalConfirmDelete
			ref="modal-remove"
			:handle-remove="deleteBanner.bind(null, id)"
			title="Remove this banner?"
			description="By removing this, all banner info will be disappeared from the list and linked widgets."
			@onSuccess="$router.push({ name: 'BannerHeroList'})"
		/>
	</form>
</template>

<script>
import { mapActions } from 'vuex';

import BannerGeneral from '@/components/BannerGeneral.vue';
import BannerDate from '@/components/BannerDate.vue';
import BannerContentWrapper from '@/components/BannerContentWrapper.vue';
import BaseMediaSelectorBox from '@/components/BaseMediaSelectorBox.vue';

import { BANNER_TYPE, BANNER_IMAGE_TYPE } from '../enums/banners';
import { convertDateTimeToUTC } from '../assets/js/helpers';

export default {
	name: 'BannerHeroForm',

	components: {
		BannerGeneral,
		BannerDate,
		BannerContentWrapper,
		BaseMediaSelectorBox,
	},

	props: {
		isEditMode: {
			type: Boolean,
			default: false,
		},
		isLoading: {
			type: Boolean,
			default: false,
		},
		isUpdating: {
			type: Boolean,
			default: false,
		},
		data: {
			type: Object,
			default: null,
		},
		bannerType: {
			type: String,
			default: BANNER_TYPE.hero,
		},
	},

	data() {
		return {
			id: null,
			name: '',
			priority: 0,
			status: true,
			startDate: null,
			startTime: '00:00',
			endDate: null,
			endTime: '23:59',
			desktopBannerTh: null,
			desktopBannerEn: null,
			mobileBannerTh: null,
			mobileBannerEn: null,
			bannerLink: '',
			desktopBackgroundColor: '#ffffff',
			mobileBackgroundColor: '#ffffff',
		};
	},

	computed: {
		widgetType() {
			const widget = {
				hero: 'hero_banner',
				highlight: 'highlight_banner',
				small: 'small_banner',
			};

			return widget[this.bannerType];
		},
	},

	watch: {
		data: {
			immediate: true,
			handler(value) {
				if (this.isEditMode && !this.isUpdating) {
					this.id = value.id;
					this.name = value.name;
					this.priority = value.priority;
					this.status = value.status;
					this.startDate = value.startDate;
					this.startTime = value.startTime;
					this.endDate = value.endDate;
					this.endTime = value.endTime;
					this.desktopBannerTh = value.banners.desktopBannerTh;
					this.desktopBannerEn = value.banners.desktopBannerEn;
					this.mobileBannerTh = value.banners.mobileBannerTh;
					this.mobileBannerEn = value.banners.mobileBannerEn;
					this.bannerLink = value.banners.bannerLink;
					this.desktopBackgroundColor = value.banners?.desktopBannerTh?.desktopBackgroundColor
						|| value.banners?.desktopBannerEn?.desktopBackgroundColor
						|| '#ffffff';
					this.mobileBackgroundColor = value.banners?.mobileBannerTh?.mobileBackgroundColor
						|| value.banners?.mobileBannerEn?.mobileBackgroundColor
						|| '#ffffff';
				}
			},
		},
	},

	methods: {
		...mapActions({
			deleteBanner: 'banners/deleteBanner',
		}),

		handleChangeEndDate(value) {
			this.endDate = value;
		},

		addBanner(file, type, link, bgColor) {
			const result = {};
			result.file = file ? file.id : null;
			result.type = type;

			if (link) {
				result.link = link;
			}

			result.background_color = bgColor || '#ffffff';

			return result;
		},

		handleSubmit() {
			const generalRef = this.$refs.general.$v;
			const dateRef = this.$refs.date.$v;

			const contentRef = this.$refs.content.$v;
			const desktopBannerThRef = this.$refs.desktopBannerTh.$v;
			const desktopBannerEnRef = this.$refs.desktopBannerEn.$v;
			const mobileBannerThRef = this.$refs.mobileBannerTh.$v;
			const mobileBannerEnRef = this.$refs.mobileBannerEn.$v;

			generalRef.$touch();
			dateRef.$touch();

			contentRef.$touch();
			desktopBannerThRef.$touch();
			desktopBannerEnRef.$touch();
			mobileBannerThRef.$touch();
			mobileBannerEnRef.$touch();

			if (
				!generalRef.$invalid &&
				!dateRef.$invalid &&
				!contentRef.$invalid &&
				!desktopBannerThRef.$invalid &&
				!desktopBannerEnRef.$invalid &&
				!mobileBannerThRef.$invalid &&
				!mobileBannerEnRef.$invalid
			) {
				const data = {
					name: this.name,
					type: BANNER_TYPE.hero,
					priority: Number(this.priority, 10),
					is_visible: this.status,
					start_at: convertDateTimeToUTC(this.startDate, this.startTime),
					end_at: convertDateTimeToUTC(this.endDate, this.endTime),
					banners: [
						this.addBanner(
							this.desktopBannerTh,
							BANNER_IMAGE_TYPE.homepage_desktop_banner_image_en,
							this.bannerLink,
							this.desktopBackgroundColor,
						),
						this.addBanner(
							this.desktopBannerEn,
							BANNER_IMAGE_TYPE.homepage_desktop_banner_image,
							this.bannerLink,
							this.desktopBackgroundColor,
						),
						this.addBanner(
							this.mobileBannerTh,
							BANNER_IMAGE_TYPE.homepage_mobile_banner_image_en,
							this.bannerLink,
							this.mobileBackgroundColor,
						),
						this.addBanner(
							this.mobileBannerEn,
							BANNER_IMAGE_TYPE.homepage_mobile_banner_image,
							this.bannerLink,
							this.mobileBackgroundColor,
						),
					],
				};

				this.$emit('onSubmit', data);
			}
		},

		handleRemove() {
			this.$emit('onRemove');
		},
	},
};
</script>

<style lang="scss" scoped>
.remove-banners {
	font-size: rem(14);
	color: $color-orange;

	align-items: center;

	cursor: pointer;
}
</style>
